
import { supabase } from "@/integrations/supabase/client";

export type TaskActivityType = 'field_update' | 'date_change' | 'shortcut_added' | 'stage_change' | 
                             'assignment_change' | 'note_added' | 'entity_association';

export interface TaskActivityLogEntry {
  taskId: number;
  activityType: TaskActivityType;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
}

export const logTaskActivity = async (entry: TaskActivityLogEntry) => {
  try {
    console.log("Logging task activity:", entry);
    
    if (!entry.taskId) {
      console.error("Cannot log activity without a task ID");
      return false;
    }
    
    // Format the entry for the database
    const { data, error } = await supabase
      .from("task_activity_log")
      .insert([{
        task_id: entry.taskId,
        activity_type: entry.activityType,
        field_name: entry.fieldName,
        old_value: entry.oldValue,
        new_value: entry.newValue,
        // Add current user ID when possible
        user_id: (await supabase.auth.getUser()).data?.user?.id
      }]);
      
    if (error) {
      console.error("Error logging task activity:", error);
      // If we get an RLS error, we'll try one more time without the user_id
      if (error.message.includes('violates row level security policy')) {
        console.warn("Trying to log activity without user_id due to RLS policy");
        const { error: retryError } = await supabase
          .from("task_activity_log")
          .insert([{
            task_id: entry.taskId,
            activity_type: entry.activityType,
            field_name: entry.fieldName,
            old_value: entry.oldValue,
            new_value: entry.newValue
          }]);
          
        if (retryError) {
          console.error("Error in retry attempt for task activity logging:", retryError);
          return false;
        }
      } else {
        return false;
      }
    }
    
    console.log("Task activity logged successfully");
    return true;
  } catch (err) {
    console.error("Exception logging task activity:", err);
    return false;
  }
};

export const logTaskFieldUpdate = async (taskId: number, fieldName: string, oldValue: any, newValue: any) => {
  console.log(`Logging field update for ${fieldName}:`, { old: oldValue, new: newValue });
  return logTaskActivity({
    taskId,
    activityType: 'field_update',
    fieldName,
    oldValue: oldValue?.toString() || '',
    newValue: newValue?.toString() || ''
  });
};

export const logTaskDateChange = async (taskId: number, fieldName: string, oldDate: Date | string | null, newDate: Date | string | null) => {
  const oldDateStr = oldDate ? new Date(oldDate).toISOString() : '';
  const newDateStr = newDate ? new Date(newDate).toISOString() : '';
  
  console.log(`Logging date change for ${fieldName}:`, { old: oldDateStr, new: newDateStr });
  return logTaskActivity({
    taskId,
    activityType: 'date_change',
    fieldName,
    oldValue: oldDateStr,
    newValue: newDateStr
  });
};

export const logTaskStageChange = async (taskId: number, oldStage: string, newStage: string) => {
  console.log(`Logging stage change:`, { old: oldStage, new: newStage });
  return logTaskActivity({
    taskId,
    activityType: 'stage_change',
    oldValue: oldStage,
    newValue: newStage
  });
};

export const logTaskAssignmentChange = async (taskId: number, oldAssigneeId: string | null, newAssigneeId: string | null) => {
  console.log(`Logging assignment change:`, { old: oldAssigneeId, new: newAssigneeId });
  return logTaskActivity({
    taskId,
    activityType: 'assignment_change',
    oldValue: oldAssigneeId || '',
    newValue: newAssigneeId || ''
  });
};
